const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getString = (value) => (value === null || value === undefined ? "" : String(value).trim());

const toNumberString = (value) => {
  const numericValue = Number.parseFloat(value);
  return Number.isFinite(numericValue) ? String(numericValue) : "";
};

const parseDateValue = (value) => {
  const dateValue = getString(value);
  const dateParts = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (dateParts) {
    const [, day, month, year] = dateParts;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const parsedDate = Date.parse(dateValue);
  return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

const splitBrands = (value) =>
  getString(value)
    .split(",")
    .map((brand) => brand.trim())
    .filter(Boolean);

const levenshteinDistance = (a = "", b = "") => {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;

  const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  const current = Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i += 1) {
    current[0] = i;

    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + cost
      );
    }

    for (let j = 0; j <= b.length; j += 1) {
      previous[j] = current[j];
    }
  }

  return previous[b.length];
};

const similarity = (a = "", b = "") => {
  const first = normalizeText(a);
  const second = normalizeText(b);

  if (!first || !second) return 0;
  if (first.includes(second) || second.includes(first)) return 1;

  const distance = levenshteinDistance(first, second);
  return 1 - distance / Math.max(first.length, second.length);
};

const getProjectMatchScore = (projectName = "", indentProjectName = "") => {
  const project = getString(projectName);
  const indentProject = getString(indentProjectName);

  if (!project || !indentProject) return 0;

  const projectTokens = project.split(/\s+/).filter((token) => normalizeText(token).length >= 3);
  const indentTokens = indentProject
    .split(/\s+/)
    .filter((token) => normalizeText(token).length >= 3);

  if (projectTokens.length === 0 || indentTokens.length === 0) {
    return similarity(project, indentProject);
  }

  const indentTokenScores = indentTokens.map((indentToken) =>
    Math.max(...projectTokens.map((projectToken) => similarity(projectToken, indentToken)))
  );

  return Math.min(...indentTokenScores);
};

export const buildIndentApiUrl = (indentApiUrl, indentId) => {
  const url = getString(indentApiUrl);
  const indentIds = parseIndentIds(indentId);
  const primaryIndentId = indentIds[0] || getString(indentId);
  const encodedIndentId = encodeURIComponent(primaryIndentId);
  const indentFilter = indentIds
    .map((currentIndentId) => `RfqNumber eq '${String(currentIndentId).replace(/'/g, "''")}'`)
    .join(" or ");

  if (!url) return "";
  if (url.includes("{indentFilter}")) return url.split("{indentFilter}").join(indentFilter);
  if (url.includes(":indentFilter")) return url.split(":indentFilter").join(indentFilter);
  if (url.includes("__INDENT_FILTER__")) return url.split("__INDENT_FILTER__").join(indentFilter);
  if (url.includes("{indentId}")) return url.split("{indentId}").join(encodedIndentId);
  if (url.includes(":indentId")) return url.split(":indentId").join(encodedIndentId);
  if (url.includes("__INDENT_ID__")) return url.split("__INDENT_ID__").join(encodedIndentId);

  if (url.endsWith("/") || url.endsWith("=")) return `${url}${encodedIndentId}`;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}indentId=${encodedIndentId}`;
};

export const parseIndentIds = (value = "") => [
  ...new Set(
    getString(value)
      .split(",")
      .map((indentId) => indentId.trim())
      .filter(Boolean)
  ),
];

export const extractIndentRecord = (responseData) => {
  const records = responseData?.d?.results || responseData?.results || responseData?.data?.results;

  if (Array.isArray(records)) return records[0] || null;
  if (responseData?.d && !Array.isArray(responseData.d.results)) return responseData.d;
  if (responseData?.data && !Array.isArray(responseData.data.results)) return responseData.data;

  return responseData || null;
};

export const extractIndentRecords = (responseData) => {
  const records = responseData?.d?.results || responseData?.results || responseData?.data?.results;

  if (Array.isArray(records)) return records.filter(Boolean);

  const record = extractIndentRecord(responseData);
  return record ? [record] : [];
};

export const mapIndentItemsToMaterials = (indentRecord) => {
  const items = indentRecord?.ItemSet?.results || indentRecord?.items || indentRecord?.materials || [];
  const indentNumber = getString(
    indentRecord?.RfqNumber || indentRecord?.indentId || indentRecord?.IndentNumber
  );
  const projectName = getString(indentRecord?.ProjectName || indentRecord?.projectName);

  if (!Array.isArray(items)) return [];

  return items
    .map((item) => ({
      code: getString(item?.Code || item?.code),
      name: getString(item?.Name || item?.name),
      quantity: toNumberString(item?.Quantity || item?.quantity),
      unit: getString(item?.Unit || item?.unit),
      brands: splitBrands(item?.Brands || item?.brands),
      specifications: getString(item?.Specification || item?.specification || item?.specifications),
      sourceIndentId: getString(item?.RfqNumber || item?.indentId) || indentNumber,
      indentNumber,
      projectName,
    }))
    .filter((material) => material.name && material.quantity && material.unit);
};

export const mapIndentItemsToLabourDetails = (indentRecord) => {
  const items = indentRecord?.ItemSet?.results || indentRecord?.items || indentRecord?.materials || [];
  const indentNumber = getString(
    indentRecord?.RfqNumber || indentRecord?.indentId || indentRecord?.IndentNumber
  );
  const projectName = getString(indentRecord?.ProjectName || indentRecord?.projectName);

  if (!Array.isArray(items)) return [];

  return items
    .map((item) => ({
      skill: getString(item?.Name || item?.name),
      quantity: toNumberString(item?.Quantity || item?.quantity),
      unit: getString(item?.Unit || item?.unit),
      budget: "",
      sourceIndentId: getString(item?.RfqNumber || item?.indentId) || indentNumber,
      indentNumber,
      projectName,
      group: getString(item?.Group || item?.group),
      wbs: getString(item?.Wbs || item?.wbs),
      plant: getString(item?.Plant || item?.plant),
      itemNumber: getString(item?.ItemNumber || item?.itemNumber),
    }))
    .filter((labour) => labour.skill && labour.quantity && labour.unit);
};

export const findMatchingProjectIds = (projects = [], projectName = "") => {
  const matches = projects
    .map((project) => ({
      project,
      score: getProjectMatchScore(project?.name, projectName),
    }))
    .filter(({ score }) => score >= 0.68)
    .sort((a, b) => b.score - a.score);

  return matches.map(({ project }) => project?._id).filter(Boolean);
};

export const findExactMatchingProjectIds = (projects = [], projectNames = []) => {
  const names = Array.isArray(projectNames) ? projectNames : [projectNames];
  const normalizedNames = new Set(
    names
      .map((name) => getString(name).toLowerCase())
      .filter(Boolean)
  );

  if (!normalizedNames.size) return [];

  return [
    ...new Set(
      projects
        .filter((project) => normalizedNames.has(getString(project?.name).toLowerCase()))
        .map((project) => project?._id)
        .filter(Boolean)
    ),
  ];
};

export const mapIndentResponseToRfqValues = ({ responseData, projects = [], rfqType = "" }) => {
  const indentRecords = extractIndentRecords(responseData);
  const indentRecord = indentRecords[0];
  const normalizedRfqType = getString(rfqType).toLowerCase();

  if (!indentRecord) {
    return {
      materials: [],
      labourDetails: [],
      projectIds: [],
      projectName: "",
    };
  }

  const projectNames = indentRecords
    .map((record) => getString(record.ProjectName || record.projectName))
    .filter(Boolean);
  const projectName = projectNames[0] || "";
  const materials = indentRecords.flatMap((record) => mapIndentItemsToMaterials(record));
  const isLabourRfq = normalizedRfqType === "labour" || normalizedRfqType === "labor";
  const labourDetails = isLabourRfq
    ? indentRecords.flatMap((record) => mapIndentItemsToLabourDetails(record))
    : [];
  const projectIds = [
    ...new Set(
      projectNames.flatMap((currentProjectName) =>
        findMatchingProjectIds(projects, currentProjectName)
      )
    ),
  ];

  return {
    rfqName: getString(indentRecord.Name || indentRecord.RfqName || indentRecord.rfqName),
    materials: isLabourRfq ? [] : materials,
    labourDetails,
    projectIds,
    projectName,
    rfqBudget: toNumberString(indentRecord.RfqBudget || indentRecord.rfqBudget),
    paymentTerms: getString(indentRecord.PaymentTerms || indentRecord.paymentTerms),
    termsAndConditions: getString(indentRecord.TermsCondition || indentRecord.TermsAndConditions),
    transportOption: getString(indentRecord.TransportOption || indentRecord.transportOption),
    businessDivision: getString(indentRecord.BusinessDivision || indentRecord.businessDivision),
    quotationDeadline: getString(indentRecord.QuotationDeadline || indentRecord.quotationDeadline),
  };
};

export const mapPendingPrResponseToRfqValues = ({ responseData }) => {
  const records = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData?.payload?.data)
      ? responseData.payload.data
      : Array.isArray(responseData)
        ? responseData
        : [];

  const materials = records
    .map((item, index) => {
      const projectName = getString(item?.Project || item?.project);
      const category = getString(item?.Category || item?.category);
      const materialName = getString(item?.Material || item?.material);

      return {
        code: getString(item?.Code || item?.code),
        name: materialName,
        quantity: toNumberString(item?.["App Qty"] || item?.AppQty || item?.quantity),
        unit: getString(item?.Tech_Unit || item?.techUnit || item?.Unit || item?.unit),
        brands: [],
        specifications: "",
        source: "pendingPr",
        sourceRowId: getString(item?.PrNo || item?.PRNo || item?.id) || `pending-pr-${index}`,
        projectName,
        category,
        priority: getString(item?.Priority || item?.priority),
        poStatus: getString(item?.["PO Status"] || item?.poStatus),
        procurementModeDate: getString(item?.ProcurementModeDate || item?.procurementModeDate),
      };
    })
    .filter((material) => material.name && material.quantity && material.unit)
    .sort(
      (firstMaterial, secondMaterial) =>
        parseDateValue(secondMaterial.procurementModeDate) -
        parseDateValue(firstMaterial.procurementModeDate)
    );

  return {
    materials,
    projectIds: [],
  };
};
